import {
  getGetSuperAdminFranchisesFranchiseIdPartnersQueryKey,
  getGetSuperAdminPartnersQueryKey,
  useDeleteSuperAdminFranchisePartnersId,
  useDeleteSuperAdminPartnersId,
  usePatchSuperAdminPartnersId,
  useGetSuperAdminFranchises,
  useGetSuperAdminFranchisesFranchiseIdPartners,
  useGetSuperAdminPartners,
  usePostSuperAdminFranchisesFranchiseIdPartners,
  usePostSuperAdminPartners,
  usePutSuperAdminPartnersIdLogin,
} from '@agos/api-client/platform'
import { apiErrorMessage } from '@agos/api-client'
import { T, formatDecimal, tt, useModalFocus } from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { PageHeader, FilterSelect, ReadFailed } from '@agos/ui'

import {
  MIN_PASSWORD,
  allocationNote,
  deleteRefusal,
  draftOf,
  emptyNewRest,
  hasLogin,
  isOverAllocated,
  loginNote,
  loginState,
  portalAccountOf,
  mayDelete,
  mayPatch,
  partnerPasswordProblem,
  newPartnerRest,
  partnerPatch,
  partnersOf,
  sharePctProblem,
  shareProblem,
  shareProblemMessage,
  sharesAreUnknown,
  sharesNote,
  sharesState,
  sharesOf,
  type Partner,
  type PartnerDraft,
} from '@agos/saas-franchises'

/**
 * The investors behind the franchises, and the share of each they hold - `SAAS.md` §3.9.
 *
 * **FB-505:** *"a partner's share decides a payout and cannot be set."* Four write endpoints with no
 * caller and no screen at all, only the partner's own portal.
 *
 * **The finding behind this screen is the arithmetic, not the absence.** `PartnersAdminService`
 * bounds each share individually and nothing checks the total per franchise; the owner's three
 * franchises each sum to exactly 100,00, held by hand. `ProfitabilityService` then reports
 * `housePct = max(0, 100 - total)`, which **clamps** - so an over-allocation quotes every partner
 * their full payout and shows the house at nought. Both the total and the refusal live here.
 */
/**
 * The four text fields §3.106 added to the detail form, with their labels.
 *
 * A table rather than four copies of the same input, and **each label inside its own `tt()` called
 * at render** - a `tt()` evaluated at import runs before a locale is chosen, which is why
 * `fieldLabel` on the pricing screen is a function too.
 */
const DETAIL_TEXTS: { field: 'phone' | 'ico' | 'dic' | 'notes'; placeholder: () => string }[] = [
  { field: 'phone', placeholder: () => tt('Telefón') },
  { field: 'ico', placeholder: () => tt('IČO') },
  { field: 'dic', placeholder: () => tt('DIČ') },
  { field: 'notes', placeholder: () => tt('Poznámka') },
]

export function PartnersPage() {
  const partnerList = useGetSuperAdminPartners()
  const franchiseList = useGetSuperAdminFranchises()
  // §3.107. **The partner list only**: the franchise read feeds the picker, whose own emptiness is
  // reported separately, and folding the two would say *no partners* when the franchises failed.
  const readFailed = partnerList.isError
  const partners = useMemo(() => partnersOf(partnerList.data), [partnerList.data])

  const franchises = useMemo(() => {
    const rows = Array.isArray(
      (franchiseList.data as { franchises?: unknown } | undefined)?.franchises,
    )
      ? (franchiseList.data as { franchises: Record<string, unknown>[] }).franchises
      : []
    return rows.flatMap((row) =>
      typeof row.id === 'number'
        ? [{ id: row.id, name: typeof row.name === 'string' ? row.name : `#${row.id}` }]
        : [],
    )
  }, [franchiseList.data])

  const [chosen, setChosen] = useState<number | null>(null)
  // The first franchise until somebody picks another - a screen about shares with no franchise
  // selected shows the one thing it exists to show, which is nothing.
  const franchiseId = chosen ?? franchises[0]?.id ?? null
  const links = useGetSuperAdminFranchisesFranchiseIdPartners(franchiseId ?? 0, {
    query: { enabled: franchiseId !== null },
  })
  const shares = useMemo(() => sharesOf(links.data), [links.data])
  /* FB-827, §3.156 - **the share read is the dangerous one, because its failure feeds both the claim and the guard.**
     `sharesOf(undefined)` is `[]`, so `allocationNote` says *Žiadny podiel nie je rozdelený - celý zisk zostáva
     firme* about a franchise where a partner holds 30 % (§3.122), every row reads *Bez podielu*, and
     `shareProblem`'s `allocatedWith(shares, …) > FULL_SHARE` clears a second 30 % because the first is not in the
     baseline. A false claim beside a guard that would catch acting on it is survivable; a false claim whose guard
     comes from the same absent payload is not. */
  /* §3.176, FB-942 - `links` is `enabled: franchiseId !== null`, so a failed **franchise** read leaves this
     query unrun, `isError` false, and the figure below claiming that nothing is allocated. *Absent is not
     false* in its query-state form: a read nobody made reports no error. */
  const shareView = sharesState({
    sharesFailed: links.isError,
    franchisesFailed: franchiseList.isError,
    franchiseCount: franchises.length,
    franchiseId,
  })
  const sharesUnknown = sharesAreUnknown(shareView)

  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetSuperAdminPartnersQueryKey() })
    if (franchiseId !== null) {
      void queryClient.invalidateQueries({
        queryKey: getGetSuperAdminFranchisesFranchiseIdPartnersQueryKey(franchiseId),
      })
    }
  }

  // Which partner's share is being edited, and to what. Keyed by partner rather than by link row,
  // because the same control adds a partner who has no link yet.
  const [editing, setEditing] = useState<{ partnerId: number; raw: string } | null>(null)
  // And which partner's own details, which is a different edit: the share lives on the franchise
  // link, the name and IBAN on the partner.
  const [detailOf, setDetailOf] = useState<{ partnerId: number; draft: PartnerDraft } | null>(null)
  /**
   * What the server refused, in the server's own words - `SAAS.md` §3.35.
   *
   * **Six mutations here had `onSuccess: refresh` and no `onError` at all.** The server can refuse
   * any of them - `Neplatný IBAN`, `Neplatné IČO`, `Neplatné DIČ` (§3.34), `Žiadne zmeny`, a name
   * over 150 characters, a short password, and §3.25's *Podiely by dohromady dali N %* - and every
   * one of those sentences went nowhere. The share button is `disabled` on its own client-side
   * `problem`, so the one refusal anybody exercised was pre-empted before the request; that is why
   * this went unnoticed and it is the arrangement §3.25 warned about.
   *
   * One banner rather than one per control: only one of these can be in flight from a single click,
   * and a refusal is cleared the moment anything succeeds.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const succeeded = () => {
    setRefused(null)
    refresh()
  }
  // `apiErrorMessage` is the shared reader, not a new one: it takes `error` off the backend's
  // `{error, code}` envelope, and its own docstring records six copies reading the wrong field once
  // already (FB-C40). The fallback covers a network drop, where there is no envelope to read.
  const refusedWith = (error: unknown) => {
    setRefused(apiErrorMessage(error, tt('Zmenu sa nepodarilo uložiť - server neodpovedal.')))
  }

  const createPartner = usePostSuperAdminPartners({
    mutation: { onSuccess: succeeded, onError: refusedWith },
  })
  const patchPartner = usePatchSuperAdminPartnersId({
    mutation: {
      // **The editor closes here and nowhere else.** It used to close on the line after `mutate`,
      // which returns before the request is sent - so it could not have been conditional on the
      // answer, and a refused correction to an IBAN closed the panel and left the old number on
      // screen with nothing said.
      onSuccess: () => {
        setDetailOf(null)
        succeeded()
      },
      onError: refusedWith,
    },
  })
  const deletePartner = useDeleteSuperAdminPartnersId({
    mutation: { onSuccess: succeeded, onError: refusedWith },
  })
  const setLogin = usePutSuperAdminPartnersIdLogin({
    mutation: { onSuccess: succeeded, onError: refusedWith },
  })
  const saveShare = usePostSuperAdminFranchisesFranchiseIdPartners({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        succeeded()
      },
      onError: refusedWith,
    },
  })
  const removeShare = useDeleteSuperAdminFranchisePartnersId({
    mutation: { onSuccess: succeeded, onError: refusedWith },
  })

  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  // §3.106 - the other six fields the create endpoint takes.
  const [newRest, setNewRest] = useState(emptyNewRest)
  const [asking, setAsking] = useState<
    | { kind: 'delete-partner'; partner: Partner }
    | { kind: 'grant-login'; partner: Partner }
    | { kind: 'clear-login'; partner: Partner }
    /**
     * §3.177, FB-945 - taking a partner's share away was one click and asked nothing.
     *
     * **A fourth kind rather than a dialog of its own**, so the question inherits this one's focus trap, its
     * `data-partner-confirm` marker and its cancel target - nothing new to select by, which is what keeps
     * `tower.spec.ts`'s existing assertions about this dialog talking about the same thing.
     *
     * It carries the **percentage** as well as the link, because that figure leaves the screen on the click:
     * the share can be re-entered, but only by somebody who knows what it was.
     */
    | { kind: 'remove-share'; partner: Partner; linkId: number; sharePct: number }
    | null
  >(null)
  const [loginEmail, setLoginEmail] = useState('')
  const [password, setPassword] = useState('')

  /* §9.489, `TODO.md` 3f - the confirmation said `aria-modal="true"` and confined nothing. This one serves
     three questions, one of which deletes a partner and one of which grants a login, so the opening focus
     belonging to *Zrušiť* rather than to the confirm is the whole point. Guarded on both mutations it can
     fire: Escape must do nothing while either is in flight (§9.480). */
  const confirmRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: confirmKeyDown } = useModalFocus(
    asking !== null,
    confirmRef,
    deletePartner.isPending || setLogin.isPending ? null : () => setAsking(null),
  )

  const shareOf = (partnerId: number) => shares.find((share) => share.partnerId === partnerId)
  const problem =
    editing === null ? null : shareProblem(editing.raw, shares, editing.partnerId)

  return (
    <main className="space-y-3" data-partners>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Partneri')} description={tt('Podiely partnerov a ich prístup do portálu.')} />

      {refused !== null && (
        <p
          className="rounded-2xl border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-500"
          data-partner-refused
          role="alert"
        >
          {refused}
        </p>
      )}
      {/* ─── The total, which is the figure the clamp was hiding ───────────── */}
      {/* Three states, three markers, **written out rather than interpolated**. `marker={shareView}` read
        * better and made §3.167's census blind to all three - it scans for `marker="literal"`, and a
        * banner whose name is computed is one no ratchet can count. The sentences are `sharesNote`'s, so
        * the wording and the withholding cannot come apart. */}
      {shareView === 'shares-unreadable' && (
        <ReadFailed marker="shares-unreadable">{sharesNote(shareView)}</ReadFailed>
      )}
      {shareView === 'franchises-unreadable' && (
        <ReadFailed marker="franchises-unreadable">{sharesNote(shareView)}</ReadFailed>
      )}
      {/* **Not a `ReadFailed`, and that is the point.** A platform with no franchises yet is an ordinary
        * state, and drawing it red with `role="alert"` would report a fault where there is none - FB-812's
        * class inverted. The figure is still withheld, because *no share is allocated* is as false about a
        * franchise that does not exist as about one whose shares could not be read. */}
      {shareView === 'no-franchises' && (
        <p className="text-xs text-muted-foreground" data-no-franchises>
          {sharesNote(shareView)}
        </p>
      )}
      <section
        className={`rounded-2xl border p-3 space-y-2 ${isOverAllocated(shares) ? 'border-red-500/40' : 'border-border'}`}
        data-allocation
        data-over-allocated={isOverAllocated(shares) ? 'yes' : 'no'}
      >
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <T>Podiely v prevádzke</T>
          </p>
          {/* §3.117 - the product's own filter select. It is a filter, not a form field: it decides
            * whose shares the panel below shows. `FilterSelect` brings the `aria-label` this had none
            * of, so a screen reader reads *what* is being chosen rather than only which franchise. */}
          <FilterSelect
            className="min-h-[32px] text-xs"
            value={franchiseId === null ? '' : String(franchiseId)}
            onChange={(chosen) => {
              setChosen(Number(chosen))
              setEditing(null)
            }}
            label={tt('Prevádzka')}
            testId="data-franchise-picker"
            options={franchises.map((franchise) => ({
              value: String(franchise.id),
              label: franchise.name,
            }))}
          />
        </div>
        <p
          className={`text-xs ${isOverAllocated(shares) ? 'text-red-500 font-semibold' : 'text-muted-foreground'}`}
          data-allocation-note
        >
          {sharesUnknown ? '' : allocationNote(shares)}
        </p>
      </section>

      {/* ─── The partners, each with their share in the chosen franchise ───── */}
      <section className="space-y-1" data-partner-list>
        {partners.map((partner) => {
          const share = shareOf(partner.id)
          const isEditing = editing?.partnerId === partner.id
          return (
            <div
              key={partner.id}
              className="rounded-xl border border-border p-2 text-xs space-y-1.5"
              data-partner={String(partner.id)}
              data-partner-active={partner.active ? 'yes' : 'no'}
            >
              <p>
                <span className="font-semibold">{partner.name}</span>
                {partner.email !== null && (
                  <span className="text-muted-foreground"> &middot; {partner.email}</span>
                )}
                {partner.iban !== null && (
                  <span className="text-muted-foreground" data-partner-iban>
                    {' '}
                    &middot; {partner.iban}
                  </span>
                )}
                {/* §3.106 - the phone is on both of the owner's two partners and was on no screen.
                  * Each of these is conditional, so a partner with none of them keeps the line it
                  * had. */}
                {partner.phone !== null && (
                  <span className="text-muted-foreground" data-partner-phone>
                    {' '}
                    &middot; {partner.phone}
                  </span>
                )}
                {partner.ico !== null && (
                  <span className="text-muted-foreground" data-partner-ico>
                    {' '}
                    &middot; <T>IČO</T> {partner.ico}
                  </span>
                )}
                {partner.dic !== null && (
                  <span className="text-muted-foreground" data-partner-dic>
                    {' '}
                    &middot; <T>DIČ</T> {partner.dic}
                  </span>
                )}
                {/* The share they default to when linked to a franchise, and the figure their own
                  * portal shows them. Drawn always rather than conditionally: 0 % is a real value
                  * and the one a Tower-created partner used to be stuck with. */}
                <span className="text-muted-foreground" data-partner-default-share>
                  {' '}
                  &middot; {formatDecimal(partner.defaultSharePct, 2)} %
                </span>
                {!partner.active && (
                  <span className="text-amber-500" data-partner-inactive>
                    {' '}
                    &middot; <T>neaktívny</T>
                  </span>
                )}
              </p>
              {/* A note somebody wrote for a reason, which both of the owner's partners have. */}
              {partner.notes !== null && (
                <p className="text-[10px] text-muted-foreground" data-partner-notes>
                  {partner.notes}
                </p>
              )}
              <p
                className="text-[10px] text-muted-foreground"
                data-partner-login={hasLogin(partner) ? 'yes' : 'no'}
                /* §3.125 - three states rather than two. `data-partner-login` stays yes/no because
                   `tower.spec.ts` branches on it, and this carries the distinction that attribute
                   cannot: an address with no password is neither. */
                data-partner-portal={portalAccountOf(partner)}
              >
                {loginState(partner)}
              </p>

              {/* The partner's own details. A separate edit from the share deliberately: the share
                * lives on the franchise link and changes per franchise, while the IBAN is one
                * account for the person. And `iban` is why this endpoint is wired at all - a screen
                * that can add a partner but never correct their account number is one typo from a
                * misdirected payment. */}
              {detailOf?.partnerId === partner.id ? (
                <div className="flex flex-wrap gap-1.5" data-partner-detail-form>
                  <input
                    data-detail-name
                    value={detailOf.draft.name}
                    onChange={(event) =>
                      setDetailOf({
                        partnerId: partner.id,
                        draft: { ...detailOf.draft, name: event.target.value },
                      })
                    }
                    placeholder={tt('Meno')}
                    className="min-h-[32px] flex-1 min-w-[8rem] rounded-lg border border-border bg-background px-2 text-xs"
                  />
                  <input
                    data-detail-email
                    type="email"
                    value={detailOf.draft.email}
                    onChange={(event) =>
                      setDetailOf({
                        partnerId: partner.id,
                        draft: { ...detailOf.draft, email: event.target.value },
                      })
                    }
                    placeholder={tt('E-mail')}
                    className="min-h-[32px] flex-1 min-w-[8rem] rounded-lg border border-border bg-background px-2 text-xs"
                  />
                  <input
                    data-detail-iban
                    value={detailOf.draft.iban}
                    onChange={(event) =>
                      setDetailOf({
                        partnerId: partner.id,
                        draft: { ...detailOf.draft, iban: event.target.value },
                      })
                    }
                    placeholder="IBAN"
                    className="min-h-[32px] flex-1 min-w-[10rem] rounded-lg border border-border bg-background px-2 text-xs"
                  />
                  {/* §3.106 - six more of the nine fields `update` patches. `ico` and `dic` are the
                    * pair §3.34 wrote a whole validation entry about, on columns no screen could
                    * ever send: `PartnersAdminService` is their only writer and the Tower is its
                    * only caller. */}
                  {DETAIL_TEXTS.map(({ field, placeholder }) => (
                    <input
                      key={field}
                      data-detail-field={field}
                      value={detailOf.draft[field]}
                      onChange={(event) =>
                        setDetailOf({
                          partnerId: partner.id,
                          draft: { ...detailOf.draft, [field]: event.target.value },
                        })
                      }
                      placeholder={placeholder()}
                      className="min-h-[32px] flex-1 min-w-[8rem] rounded-lg border border-border bg-background px-2 text-xs"
                    />
                  ))}
                  <label className="text-[10px]">
                    <input
                      data-detail-field="sharePct"
                      inputMode="decimal"
                      value={detailOf.draft.sharePct}
                      onChange={(event) =>
                        setDetailOf({
                          partnerId: partner.id,
                          draft: { ...detailOf.draft, sharePct: event.target.value },
                        })
                      }
                      placeholder={tt('Podiel %')}
                      className="min-h-[32px] w-24 rounded-lg border border-border bg-background px-2 text-xs"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-[10px]">
                    <input
                      type="checkbox"
                      data-detail-active
                      checked={detailOf.draft.active}
                      onChange={(event) =>
                        setDetailOf({
                          partnerId: partner.id,
                          draft: { ...detailOf.draft, active: event.target.checked },
                        })
                      }
                    />
                    <span>
                      <T>Aktívny</T>
                    </span>
                  </label>
                  {/* What inactive *means*, which is not guessable from the word: it is the portal
                    * sign-in gate. `PartnerPortalService` refuses an inactive partner at the login
                    * and again at the `me` read, so the shares stay and the access goes. */}
                  {!detailOf.draft.active && (
                    <p className="w-full text-[10px] text-amber-500" data-detail-inactive-note>
                      <T>
                        Neaktívny partner sa nedostane do portálu. Podiely a faktúry zostávajú
                        nezmenené.
                      </T>
                    </p>
                  )}
                  {sharePctProblem(detailOf.draft.sharePct) !== '' && (
                    <p className="w-full text-[10px] text-red-500" data-detail-share-problem>
                      {sharePctProblem(detailOf.draft.sharePct)}
                    </p>
                  )}
                  <button
                    type="button"
                    data-detail-save
                    disabled={!mayPatch(partner, detailOf.draft) || patchPartner.isPending}
                    onClick={() => {
                      // **Only what changed.** `update` builds its `SET` clause from the keys
                      // present, so a whole-row send writes every column including the untouched.
                      patchPartner.mutate({
                        id: partner.id,
                        data: partnerPatch(partner, detailOf.draft),
                      })
                    }}
                    className="px-2 py-1 rounded-lg border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold disabled:opacity-40"
                  >
                    <T>Uložiť</T>
                  </button>
                  <button
                    type="button"
                    data-detail-cancel
                    onClick={() => setDetailOf(null)}
                    className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                  >
                    <T>Zrušiť</T>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-partner-edit={String(partner.id)}
                  onClick={() => setDetailOf({ partnerId: partner.id, draft: draftOf(partner) })}
                  className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                >
                  <T>Upraviť údaje</T>
                </button>
              )}

              {/* The share, as a control rather than a column only SQL could set. */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span data-partner-share={String(partner.id)}>
                  {sharesUnknown ? (
                    <span className="text-muted-foreground" data-share-unknown={String(partner.id)}>
                      <T>Podiel neznámy</T>
                    </span>
                  ) : share === undefined ? (
                    <span className="text-muted-foreground">
                      <T>Bez podielu</T>
                    </span>
                  ) : (
                    `${formatDecimal(share.sharePct, 2)} %`
                  )}
                </span>
                {isEditing ? (
                  <>
                    <input
                      data-share-input
                      inputMode="decimal"
                      value={editing.raw}
                      onChange={(event) =>
                        setEditing({ partnerId: partner.id, raw: event.target.value })
                      }
                      className="w-20 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
                    />
                    <button
                      type="button"
                      data-share-save
                      // **Refused, not warned.** The server would accept it and
                      // `ProfitabilityService` would quote payouts that cannot all be made.
                      disabled={problem !== null || saveShare.isPending || franchiseId === null}
                      onClick={() => {
                        if (franchiseId === null) return
                        saveShare.mutate({
                          franchiseId,
                          data: {
                            partnerId: partner.id,
                            sharePct: Number(editing.raw.trim().replace(',', '.')),
                          },
                        })
                      }}
                      className="px-2 py-1 rounded-lg border border-primary/40 bg-primary/10 text-primary text-[10px] font-bold disabled:opacity-40"
                    >
                      <T>Uložiť</T>
                    </button>
                    <button
                      type="button"
                      data-share-cancel
                      onClick={() => setEditing(null)}
                      className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                    >
                      <T>Zrušiť</T>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      data-share-edit={String(partner.id)}
                      /* §9.540's rule - a check that cannot describe what it is checking against must not be
                         offered. `shareProblem` would clear a second 30 % from an empty baseline. */
                      disabled={sharesUnknown}
                      onClick={() =>
                        setEditing({
                          partnerId: partner.id,
                          raw: String(share?.sharePct ?? partner.defaultSharePct),
                        })
                      }
                      className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                    >
                      <T>Zmeniť podiel</T>
                    </button>
                    {share?.linkId != null && (
                      <button
                        type="button"
                        data-share-remove={String(partner.id)}
                        disabled={removeShare.isPending}
                        /* §3.177 - it asked nothing, while the delete two buttons along opens a confirmation
                           and `deleteRefusal` sends an operator here to clear a share first. */
                        onClick={() =>
                          setAsking({
                            kind: 'remove-share',
                            partner,
                            linkId: share.linkId as number,
                            sharePct: share.sharePct,
                          })
                        }
                        /* **Red, like the sibling delete on this screen and like legacy's own `Trash2`
                           button.** This was `border-border` with no icon - identical to *Zmeniť podiel*
                           beside it, which only opens the editor. */
                        className="px-2 py-1 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-semibold disabled:opacity-40"
                      >
                        <T>Odobrať podiel</T>
                      </button>
                    )}
                  </>
                )}
              </div>
              {isEditing && problem !== null && (
                <p className="text-[10px] text-red-500" data-share-problem={problem}>
                  {shareProblemMessage(problem, shares, partner.id, editing.raw)}
                </p>
              )}

              {/* The sign-in and the removal, both guarded the way §3.7's admin actions are. */}
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  data-partner-login-toggle={String(partner.id)}
                  onClick={() => {
                    setLoginEmail(partner.loginEmail ?? partner.email ?? '')
                    setPassword('')
                    setAsking({
                      kind: hasLogin(partner) ? 'clear-login' : 'grant-login',
                      partner,
                    })
                  }}
                  className="px-2 py-1 rounded-lg border border-border text-[10px] font-semibold"
                >
                  {hasLogin(partner) ? <T>Odobrať prihlásenie</T> : <T>Dať prihlásenie</T>}
                </button>
                <button
                  type="button"
                  data-partner-delete={String(partner.id)}
                  disabled={!mayDelete(partner.id, shares) || deletePartner.isPending}
                  onClick={() => setAsking({ kind: 'delete-partner', partner })}
                  className="px-2 py-1 rounded-lg border border-red-500/40 text-red-500 text-[10px] font-bold disabled:opacity-40"
                >
                  <T>Zmazať</T>
                </button>
              </div>
              {/* Why it is off, rather than a dead button. The cascade is the reason: deleting a
                * partner takes their share rows with them and nothing says the share was there. */}
              {!mayDelete(partner.id, shares) && (
                <p className="text-[10px] text-muted-foreground" data-partner-delete-refusal>
                  {deleteRefusal(partner.id, shares)}
                </p>
              )}
            </div>
          )
        })}
        {/* §3.107 - the list is short or it is unanswered, and *Žiadni partneri* cannot tell an
          * operator which. §3.82 made this argument about seven screens and never reached this one,
          * two directories away from the ones it swept. */}
        {readFailed && (
          <ReadFailed marker="partners-error"><T>Zoznam partnerov sa nepodarilo načítať.</T></ReadFailed>
        )}
        {partners.length === 0 && !readFailed && (
          <p className="text-xs text-muted-foreground" data-no-partners>
            <T>Žiadni partneri.</T>
          </p>
        )}
      </section>

      {/* ─── Adding one ────────────────────────────────────────────────────── */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-partner-new>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Nový partner</T>
        </p>
        <div className="flex flex-wrap gap-1.5">
          <input
            data-field="name"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder={tt('Meno')}
            className="min-h-[36px] flex-1 min-w-[10rem] rounded-xl border border-border bg-background px-3 text-xs"
          />
          <input
            data-field="email"
            type="email"
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            placeholder={tt('E-mail')}
            className="min-h-[36px] flex-1 min-w-[10rem] rounded-xl border border-border bg-background px-3 text-xs"
          />
          {/* §3.106 - the create form took 2 of the 9 fields the service writes, so a partner
            * added here had no phone, no identifiers, no IBAN, no note, and a **0 %** default
            * share, which is the column default and the figure their own portal then shows them.
            * Both of the owner's two partners have a non-default share (30 % and 50 %), which is
            * what says 0 was never the right thing to leave. */}
          {DETAIL_TEXTS.map(({ field, placeholder }) => (
            <input
              key={field}
              data-field={field}
              value={newRest[field]}
              onChange={(event) => setNewRest({ ...newRest, [field]: event.target.value })}
              placeholder={placeholder()}
              className="min-h-[36px] flex-1 min-w-[8rem] rounded-xl border border-border bg-background px-3 text-xs"
            />
          ))}
          <input
            data-field="iban"
            value={newRest.iban}
            onChange={(event) => setNewRest({ ...newRest, iban: event.target.value })}
            placeholder="IBAN"
            className="min-h-[36px] flex-1 min-w-[10rem] rounded-xl border border-border bg-background px-3 text-xs"
          />
          {/* **No share box here, deliberately, and this is §3.9's decision rather than an
            * omission.** Its own e2e case states it - *"a `default` one set here is the field that
            * lets somebody believe they have allocated something they have not"* - and the panel
            * below already says a share belongs to a franchise. §3.106 adds the field to the
            * **edit** form instead, which is a different question: the figure is read by
            * `PartnerPortalService` and shown to the partner, and until now it could not be
            * corrected at all. Both of the owner's two partners carry a non-default one. */}
          <button
            type="button"
            data-partner-create
            disabled={newName.trim() === '' || createPartner.isPending}
            onClick={() => {
              createPartner.mutate({
                data: {
                  name: newName.trim(),
                  ...(newEmail.trim() === '' ? {} : { email: newEmail.trim() }),
                  // Only what was typed. `create` defaults every one of these, so sending a blank
                  // would store an empty string where the column means *not known*.
                  ...newPartnerRest(newRest),
                },
              })
              setNewName('')
              setNewEmail('')
              setNewRest(emptyNewRest())
            }}
            className="px-3 py-1.5 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
          >
            <T>Pridať</T>
          </button>
        </div>
        {/* The share is deliberately not set here: it belongs to a franchise, and a *default* share
          * with no franchise chosen is the field that lets somebody think they have allocated one. */}
        <p className="text-[10px] text-muted-foreground">
          <T>Podiel sa nastavuje pri prevádzke, nie pri partnerovi.</T>
        </p>
      </section>

      {asking !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={
            asking.kind === 'delete-partner'
              ? tt('Zmazať partnera')
              : asking.kind === 'remove-share'
                ? tt('Odobrať podiel')
                : tt('Prihlásenie partnera')
          }
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-partner-confirm={asking.kind}
        >
          <div
            ref={confirmRef}
            onKeyDown={confirmKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
          >
            <p className="text-sm" data-partner-confirm-text>
              {asking.kind === 'delete-partner'
                ? `${tt('Zmazať partnera')} ${asking.partner.name}?`
                : asking.kind === 'remove-share'
                  ? /* **The figure is named, because it is what leaves the screen.** The share can be
                       re-entered; knowing what it was is the part a click destroys, which is the
                       *bez záznamu* `deleteRefusal` is worried about. */
                    `${tt('Odobrať partnerovi')} ${asking.partner.name} ${tt('podiel')} ${formatDecimal(asking.sharePct, 2)} %? ${tt('Tento zisk potom zostane firme.')}`
                  : loginNote(asking.kind === 'grant-login')}
            </p>

            {asking.kind === 'grant-login' && (
              <div className="space-y-1.5">
                <input
                  data-login-email
                  type="email"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  placeholder={tt('E-mail')}
                  className="w-full min-h-[36px] rounded-xl border border-border bg-background px-3 text-xs"
                />
                <input
                  data-login-password
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={tt('Heslo')}
                  className="w-full min-h-[36px] rounded-xl border border-border bg-background px-3 text-xs"
                />
                {partnerPasswordProblem(password) !== '' && password !== '' && (
                  <p className="text-[10px] text-red-500" data-login-password-problem>
                    {partnerPasswordProblem(password)}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  <T>Najmenej</T> {MIN_PASSWORD} <T>znakov</T>
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                data-partner-cancel
                data-dialog-cancel
                onClick={() => setAsking(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-partner-confirm-go
                disabled={
                  (asking.kind === 'grant-login' &&
                    (partnerPasswordProblem(password) !== '' || loginEmail.trim() === '')) ||
                  deletePartner.isPending ||
                  setLogin.isPending ||
                  removeShare.isPending
                }
                onClick={() => {
                  if (asking.kind === 'delete-partner') {
                    deletePartner.mutate({ id: asking.partner.id })
                  } else if (asking.kind === 'remove-share') {
                    removeShare.mutate({ id: asking.linkId })
                  } else if (asking.kind === 'grant-login') {
                    setLogin.mutate({
                      id: asking.partner.id,
                      data: { loginEmail: loginEmail.trim(), password },
                    })
                  } else {
                    // `clearLogin` removes the address **and** the hash, which is the only way to
                    // shut a partner out without deleting the payout history attached to them.
                    setLogin.mutate({ id: asking.partner.id, data: { clearLogin: true } })
                  }
                  setAsking(null)
                  setPassword('')
                }}
                className="flex-1 min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                <T>Potvrdiť</T>
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
